/**
 * useAllBalancerTasks Hook
 *
 * Loads wallet-balancer tasks for all projects via React Query.
 * Avoids persistent IndexedDB caching; relies on in-memory query cache only.
 *
 * Consumers can explicitly refresh a single project's data (e.g. when user expands a project).
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootStore } from 'store';

import { BALANCER_TASKS_RELOAD } from 'shared/consts/reload';
import { Bus } from 'tools';

import { getBalancerTask, WALLET_BALANCER_QUERY_KEYS } from '../api';
import type { TaskBalancer } from '../model/types';

export interface EnrichedBalancerTask extends TaskBalancer {
  projectId: number;
  projectName: string;
  isArchived: boolean;
}

type UseAllBalancerTasksResult = {
  balancers: EnrichedBalancerTask[];
  isLoading: boolean;
  isError: boolean;
  refreshProject: (projectId: number) => Promise<void>;
};

const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_QUERY_LIMIT = 1000;

export const useAllBalancerTasks = (enabled = true): UseAllBalancerTasksResult => {
  const projects = useSelector((state: RootStore) => state.projects.projects);
  const archivedProjects = useSelector((state: RootStore) => state.projects.archivedProjects);

  const queryClient = useQueryClient();

  // Only proceed if projects have been loaded (not null) AND fetching is enabled
  const projectsLoaded = projects !== null;

  const allProjects = useMemo(() => {
    if (!projectsLoaded) return [];
    const active = projects.map(p => ({ ...p, isArchived: false }));
    const archived = (archivedProjects ?? []).map(p => ({ ...p, isArchived: true }));
    return [...active, ...archived];
  }, [projects, archivedProjects, projectsLoaded]);

  const queries = useQueries({
    queries:
      enabled && projectsLoaded
        ? allProjects.map(project => {
            const params = { projectId: project.id, limit: DEFAULT_QUERY_LIMIT, offset: 0 };

            return {
              queryKey: WALLET_BALANCER_QUERY_KEYS.list(params),
              queryFn: async () => {
                const response = await getBalancerTask(params);

                if (!response.isSuccess || !response.data || !Array.isArray(response.data.tasks)) {
                  throw new Error(response.errorMessage || 'Failed to fetch balancer tasks');
                }

                return response.data.tasks;
              },
              staleTime: DEFAULT_STALE_TIME_MS,
            };
          })
        : [],
  });

  const refreshProject = useCallback(
    async (projectId: number): Promise<void> => {
      if (!enabled) return;

      const params = { projectId, limit: DEFAULT_QUERY_LIMIT, offset: 0 };
      await queryClient.invalidateQueries({
        queryKey: WALLET_BALANCER_QUERY_KEYS.list(params),
      });
      await queryClient.refetchQueries({
        queryKey: WALLET_BALANCER_QUERY_KEYS.list(params),
      });
    },
    [enabled, queryClient],
  );

  useEffect(() => {
    if (!enabled) return;

    const handleReload = async (payload?: unknown) => {
      if (!projectsLoaded) return;

      const projectId = typeof payload === 'number' ? payload : null;

      if (projectId === null) {
        await queryClient.invalidateQueries({ queryKey: WALLET_BALANCER_QUERY_KEYS.all });
        await queryClient.refetchQueries({ queryKey: WALLET_BALANCER_QUERY_KEYS.all });
        return;
      }

      await refreshProject(projectId);
    };

    Bus.on(BALANCER_TASKS_RELOAD, handleReload);
    return () => {
      Bus.off(BALANCER_TASKS_RELOAD, handleReload);
    };
  }, [enabled, projectsLoaded, queryClient, refreshProject]);

  const balancers = useMemo(() => {
    const result: EnrichedBalancerTask[] = [];

    queries.forEach((query, index) => {
      const items = query.data;
      if (!Array.isArray(items) || items.length === 0) return;

      const project = allProjects[index];
      if (!project) return;

      for (const task of items) {
        result.push({
          ...task,
          projectId: project.id,
          projectName: project.name,
          isArchived: project.isArchived,
        });
      }
    });

    return result;
  }, [allProjects, queries]);

  const isLoading = enabled && (!projectsLoaded || queries.some(query => query.isLoading));
  const isError = queries.some(query => query.isError);

  return { balancers, isLoading, isError, refreshProject };
};
